import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { AlertTriangle, Plus, ChevronDown } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Checkbox,
  Badge,
} from '@roger/ui-components';
import {
  getPatientAllergies,
  createPatientAllergy,
  createUserDefinedPatientAllergy,
  deletePatientAllergy,
  searchAllergies,
  getUserDefinedAllergies,
  updatePatient,
  client,
} from '../services/api';
import { useToast } from '../hooks/useToastContext';
import type {
  PatientResponse,
  PatientAllergyResponse,
} from '../services/api/types.gen';

interface PatientAllergiesProps {
  patient: PatientResponse;
}

interface AllergyFormData {
  allergy_name: string;
  reaction: string;
}

interface AllergyOption {
  id: string;
  name: string;
}

const PatientAllergies: React.FC<PatientAllergiesProps> = ({ patient }) => {
  const { success } = useToast();
  const [patientAllergies, setPatientAllergies] = useState<
    PatientAllergyResponse[]
  >([]);
  const [allergyOptions, setAllergyOptions] = useState<AllergyOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<AllergyOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nkaUpdating, setNkaUpdating] = useState(false);
  const [nkaChecked, setNkaChecked] = useState(
    patient.no_known_allergies ?? false
  );
  const [addExternalMode, setAddExternalMode] = useState(false);
  const [externalAllergyOptions, setExternalAllergyOptions] = useState<
    AllergyOption[]
  >([]);
  const [filteredExternalOptions, setFilteredExternalOptions] = useState<
    AllergyOption[]
  >([]);
  const [loadingExternalOptions, setLoadingExternalOptions] = useState(true);
  const [selectedExternalAllergyId, setSelectedExternalAllergyId] = useState<
    string | null
  >(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasAllergies = patientAllergies.length > 0;

  useEffect(() => {
    setNkaChecked(patient.no_known_allergies ?? false);
  }, [patient.no_known_allergies]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm<AllergyFormData>({
    defaultValues: {
      allergy_name: '',
      reaction: '',
    },
  });

  const allergyNameValue = watch('allergy_name');

  const fetchUserDefinedAllergyOptions = async () => {
    try {
      setLoadingExternalOptions(true);
      const response = await getUserDefinedAllergies({
        client,
      });
      if (response?.data) {
        const options = response.data.map(allergy => ({
          id: allergy.id,
          name: allergy.name,
        }));
        setExternalAllergyOptions(options);
        setFilteredExternalOptions(options);
      }
    } catch (err) {
      console.error('Error fetching user-defined allergy options:', err);
    } finally {
      setLoadingExternalOptions(false);
    }
  };

  useEffect(() => {
    fetchAllergies();
    fetchAllergyOptions();
    fetchUserDefinedAllergyOptions();
  }, [patient.id]);

  const fetchAllergies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPatientAllergies({
        path: { patient_id: patient.id },
        client,
      });
      if (response?.data) {
        setPatientAllergies(response.data);
      }
    } catch (err) {
      console.error('Error fetching patient allergies:', err);
      setError('Failed to load allergies');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllergyOptions = async () => {
    try {
      setLoadingOptions(true);
      const response = await searchAllergies({
        client,
      });
      if (response?.data) {
        const options = response.data.map(allergy => ({
          id: allergy.id,
          name: allergy.name,
        }));
        setAllergyOptions(options);
        setFilteredOptions(options);
      }
    } catch (err) {
      console.error('Error fetching allergy options:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Filter options based on input
  useEffect(() => {
    if (!allergyNameValue) {
      setFilteredOptions(allergyOptions);
      setFilteredExternalOptions(externalAllergyOptions);
      setHighlightedIndex(0);
      return;
    }

    const query = allergyNameValue.toLowerCase();
    if (addExternalMode) {
      const filtered = externalAllergyOptions.filter(option =>
        option.name.toLowerCase().includes(query)
      );
      setFilteredExternalOptions(filtered);
    } else {
      const filtered = allergyOptions.filter(option =>
        option.name.toLowerCase().includes(query)
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(0);
  }, [
    allergyNameValue,
    allergyOptions,
    externalAllergyOptions,
    addExternalMode,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSubmit = async (data: AllergyFormData) => {
    if (!data.allergy_name || !data.reaction) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      if (addExternalMode) {
        if (selectedExternalAllergyId) {
          await createPatientAllergy({
            path: { patient_id: patient.id },
            body: {
              allergy_id: selectedExternalAllergyId,
              reaction: data.reaction,
            },
            client,
          });
        } else {
          await createUserDefinedPatientAllergy({
            path: { patient_id: patient.id },
            body: {
              allergy_name: data.allergy_name,
              reaction: data.reaction,
            },
            client,
          });
        }
      } else {
        const selectedAllergy = allergyOptions.find(
          a => a.name.toLowerCase() === data.allergy_name.toLowerCase()
        );

        if (!selectedAllergy) {
          setError(
            'Please select an allergy from the list or use "Add allergies not in list"'
          );
          return;
        }

        await createPatientAllergy({
          path: { patient_id: patient.id },
          body: {
            allergy_id: selectedAllergy.id,
            reaction: data.reaction,
          },
          client,
        });
      }

      // Reset form and refresh allergies list
      reset();
      setSelectedExternalAllergyId(null);
      await fetchAllergies();
    } catch (err) {
      console.error('Error creating allergy:', err);
      setError('Failed to add allergy');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (patientAllergyId: string, allergyName: string) => {
    try {
      setDeletingId(patientAllergyId);
      setError(null);
      await deletePatientAllergy({
        path: { patient_allergy_id: patientAllergyId },
        client,
      });
      success(`Successfully deleted patient allergy: ${allergyName}`);
      await fetchAllergies();
    } catch (err) {
      console.error('Error deleting allergy:', err);
      setError('Failed to delete allergy');
    } finally {
      setDeletingId(null);
    }
  };

  const onNkaChange = async (checked: boolean | 'indeterminate') => {
    const value = checked === true;
    try {
      setNkaUpdating(true);
      setError(null);
      await updatePatient({
        path: { patient_id: patient.id },
        body: { no_known_allergies: value },
        client,
      });
      setNkaChecked(value);
      success(
        value ? 'Marked as no known allergies' : 'Cleared no known allergies'
      );
    } catch (err) {
      console.error('Error updating no known allergies:', err);
      setError('Failed to update no known allergies');
    } finally {
      setNkaUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <AlertTriangle className='h-5 w-5' />
          Allergies
        </CardTitle>
        <CardDescription>
          Patient allergy information and reactions for {patient.first_name}
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-6'>
        <div
          className='flex items-center space-x-2'
          title={
            hasAllergies
              ? 'Can not select if the patient has any allergies'
              : undefined
          }
        >
          <Checkbox
            id='nka'
            checked={nkaChecked}
            disabled={hasAllergies || nkaUpdating}
            onCheckedChange={onNkaChange}
            aria-label='NKA (Food / Drug / Latex / Environmental)'
          />
          <label
            htmlFor='nka'
            className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
          >
            NKA (Food / Drug / Latex / Environmental)
          </label>
        </div>

        {!nkaChecked && (
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              {!addExternalMode ? (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setAddExternalMode(true);
                    setSelectedExternalAllergyId(null);
                  }}
                >
                  Add allergies not in list
                </Button>
              ) : (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setAddExternalMode(false);
                    setSelectedExternalAllergyId(null);
                  }}
                >
                  Add from list
                </Button>
              )}
            </div>

            {/* Add Allergy Form */}
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
              {addExternalMode && (
                <Badge
                  variant='outline'
                  className='bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800'
                >
                  Make sure to check this allergy is not in our system
                </Badge>
              )}
              <div className='grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-4 items-end'>
                <div className='space-y-2' ref={dropdownRef}>
                  <label
                    htmlFor='allergy_name'
                    className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                  >
                    Allergen <span className='text-destructive'>*</span>
                  </label>
                  <div className='relative'>
                    <Controller
                      name='allergy_name'
                      control={control}
                      rules={{ required: 'Allergen is required' }}
                      render={({ field }) => {
                        const options = addExternalMode
                          ? filteredExternalOptions
                          : filteredOptions;
                        const isLoading = addExternalMode
                          ? loadingExternalOptions
                          : loadingOptions;
                        return (
                          <>
                            <div className='relative'>
                              <Input
                                {...field}
                                id='allergy_name'
                                placeholder='Type or select allergen...'
                                disabled={isLoading || submitting}
                                onChange={e => {
                                  field.onChange(e);
                                  if (addExternalMode) {
                                    setSelectedExternalAllergyId(null);
                                  }
                                }}
                                onFocus={() => setShowDropdown(true)}
                                onKeyDown={e => {
                                  if (
                                    e.key === 'Enter' &&
                                    options.length > 0 &&
                                    showDropdown
                                  ) {
                                    e.preventDefault();
                                    const selected = options[highlightedIndex];
                                    setValue('allergy_name', selected.name);
                                    if (addExternalMode) {
                                      setSelectedExternalAllergyId(selected.id);
                                    }
                                    setShowDropdown(false);
                                  } else if (e.key === 'Escape') {
                                    setShowDropdown(false);
                                  } else if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setShowDropdown(true);
                                    setHighlightedIndex(prev =>
                                      prev < options.length - 1
                                        ? prev + 1
                                        : prev
                                    );
                                  } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setHighlightedIndex(prev =>
                                      prev > 0 ? prev - 1 : 0
                                    );
                                  }
                                }}
                              />
                              <ChevronDown className='absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none' />
                            </div>
                            {showDropdown && options.length > 0 && (
                              <div className='absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-auto'>
                                {options.map((option, index) => (
                                  <button
                                    key={option.id}
                                    type='button'
                                    className={`w-full px-3 py-2 text-left text-sm cursor-pointer ${
                                      index === highlightedIndex
                                        ? 'bg-accent text-accent-foreground'
                                        : 'hover:bg-accent/50'
                                    }`}
                                    onClick={() => {
                                      setValue('allergy_name', option.name);
                                      if (addExternalMode) {
                                        setSelectedExternalAllergyId(option.id);
                                      }
                                      setShowDropdown(false);
                                    }}
                                    onMouseEnter={() =>
                                      setHighlightedIndex(index)
                                    }
                                  >
                                    {option.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </div>
                  <div className='min-h-5'>
                    {errors.allergy_name && (
                      <p className='text-sm text-destructive'>
                        {errors.allergy_name.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className='space-y-2'>
                  <label
                    htmlFor='reaction'
                    className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                  >
                    Reaction <span className='text-destructive'>*</span>
                  </label>
                  <Input
                    id='reaction'
                    placeholder='Enter reaction...'
                    disabled={submitting}
                    {...register('reaction', {
                      required: 'Reaction is required',
                    })}
                  />
                  <div className='min-h-5'>
                    {errors.reaction && (
                      <p className='text-sm text-destructive'>
                        {errors.reaction.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className='space-y-2'>
                  <label
                    className='text-sm font-medium leading-none block invisible'
                    aria-hidden
                  >
                    &#8203;
                  </label>
                  <Button
                    type='submit'
                    disabled={
                      submitting || (!addExternalMode && loadingOptions)
                    }
                  >
                    <Plus className='h-4 w-4 mr-2' />
                    {submitting
                      ? 'Adding...'
                      : addExternalMode
                        ? 'Add External'
                        : 'Add Allergy'}
                  </Button>
                  <div className='min-h-5' />
                </div>
              </div>
            </form>
            {/* Allergies List */}
            <div className='border-t pt-4'>
              {loading ? (
                <div className='text-center py-8 text-muted-foreground'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
                  <p>Loading allergies...</p>
                </div>
              ) : error ? (
                <div className='text-center py-8 text-muted-foreground'>
                  <AlertTriangle className='h-12 w-12 mx-auto mb-4 opacity-50 text-destructive' />
                  <p>{error}</p>
                </div>
              ) : (
                <div className='space-y-2'>
                  <div className='grid grid-cols-[1fr_1fr_auto] gap-4 px-4 font-medium text-sm text-muted-foreground'>
                    <div>Allergen</div>
                    <div>Reaction</div>
                    <div className='w-[70px]'></div>
                  </div>

                  {patientAllergies.length === 0 ? (
                    <div className='text-center py-8 text-muted-foreground border rounded-lg bg-muted/10'>
                      <p>No allergies on record</p>
                    </div>
                  ) : (
                    <div className='space-y-2'>
                      {patientAllergies.map(allergy => (
                        <div
                          key={allergy.id}
                          className='grid grid-cols-[1fr_1fr_auto] gap-4 items-center p-4 border rounded-lg bg-muted/50'
                        >
                          <div className='font-semibold'>
                            {allergy.allergy?.name || 'Unknown'}
                          </div>
                          <div className='text-sm'>{allergy.reaction}</div>
                          <Button
                            type='button'
                            variant='destructive'
                            size='sm'
                            disabled={deletingId === allergy.id}
                            onClick={() =>
                              onDelete(
                                allergy.id,
                                allergy.allergy?.name || 'Unknown'
                              )
                            }
                          >
                            {deletingId === allergy.id
                              ? 'Deleting...'
                              : 'Delete'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientAllergies;
